import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';

interface Template {
  id: string;
  name: string;
  description?: string;
  category: string;
  isSystem: boolean;
}

interface Production {
  id: string;
  title: string;
}

export default function DocumentNew() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const preselectedProductionId = searchParams.get('productionId');

  const [title, setTitle] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [productionId, setProductionId] = useState(preselectedProductionId || '');
  const [error, setError] = useState<string | null>(null);

  // Fetch available templates
  const { data: templatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<{ templates: Template[] }>('/documents/templates/list'),
  });

  // Fetch productions for optional linking
  const { data: productionsData, isLoading: productionsLoading } = useQuery({
    queryKey: ['productions'],
    queryFn: () => api.get<{ productions: Production[] }>('/productions'),
  });

  const templates = templatesData?.data.templates || [];
  const productions = productionsData?.data.productions || [];

  const createMutation = useMutation({
    mutationFn: (data: { templateId: string; title: string; productionId?: string }) =>
      api.post<{ document: { id: string } }>('/documents', data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      navigate(`/documents/${response.data.document.id}`);
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to create document');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!templateId) {
      setError('Please select a template');
      return;
    }

    if (!title.trim()) {
      setError('Please enter a document title');
      return;
    }

    createMutation.mutate({
      templateId,
      title: title.trim(),
      ...(productionId && { productionId }),
    });
  };

  const selectedTemplate = templates.find(t => t.id === templateId);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Create New Document</h1>
      </div>

      <form onSubmit={handleSubmit} className="form">
        {error && <div className="error-message">{error}</div>}

        <div className="form-group">
          <label htmlFor="title">Document Title *</label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Model Release - Jane Doe - Jan 2025"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="template">Document Template *</label>
          {templatesLoading ? (
            <p>Loading templates...</p>
          ) : templates.length === 0 ? (
            <p className="text-muted">No templates available. Contact your administrator.</p>
          ) : (
            <select
              id="template"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              required
            >
              <option value="">Select a template...</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name} {template.isSystem ? '(System)' : ''}
                </option>
              ))}
            </select>
          )}
          {selectedTemplate?.description && (
            <p className="help-text">{selectedTemplate.description}</p>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="production">Link to Production (Optional)</label>
          {productionsLoading ? (
            <p>Loading productions...</p>
          ) : (
            <select
              id="production"
              value={productionId}
              onChange={(e) => setProductionId(e.target.value)}
            >
              <option value="">No production linked</option>
              {productions.map((production) => (
                <option key={production.id} value={production.id}>
                  {production.title}
                </option>
              ))}
            </select>
          )}
          <p className="help-text">
            Linking to a production will pre-fill relevant details in the document.
          </p>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create Document'}
          </button>
        </div>
      </form>
    </div>
  );
}
